import { lintPcre } from "utils/pcreLint";
import { RefinementCtx } from "zod";

/**
 * Frontend checks of regex patterns. For a BE regex (`validation_regex`, wrapped into `/<value>/` and run by PCRE)
 * this is only a temporary stand-in for a BE validation: the browser engine differs from PCRE, so a pattern can
 * pass here and still behave differently on BE (or the other way round).
 */
/** A slash is escaped only by an odd number of backslashes before it: `a\\/b` still has a bare slash. */
export const hasUnescapedSlash = (value: string): boolean => {
    let backslashes = 0;
    for (const char of value) {
        if (char === "/" && backslashes % 2 === 0) {
            return true;
        }
        backslashes = char === "\\" ? backslashes + 1 : 0;
    }
    return false;
};

/** Whether the browser can compile the pattern, the way a frontend regex is used. */
export const isValidRegex = (value: string): boolean => {
    try {
        new RegExp(value);
        return true;
    } catch {
        return false;
    }
};

/**
 * Adds a zod issue at `path` if `value` isn't a valid BE `/<value>/` PCRE pattern (unescaped slash, or a
 * structural mistake `lintPcre` catches). Shared by every CC field that holds a regex BE runs through PCRE
 * (Connection Creator modify/condition operators), same as `validation_regex` on form fields.
 */
export const addPcreRegexIssue = (value: string, ctx: RefinementCtx, path: (string | number)[] = []): void => {
    if (!value) {
        return;
    }
    if (hasUnescapedSlash(value)) {
        ctx.addIssue({ code: "custom", path, message: "forms.general-validation.regex_slash" });
        return;
    }
    const [firstError] = lintPcre(value).errors;
    if (firstError) {
        ctx.addIssue({ code: "custom", path, message: `forms.general-validation.regex_${firstError}` });
    }
};
